import React, { useState } from "react";
import { BUTTON_THEME, Button, ButtonProps } from "App/core/Button";
import { ReactComponent as DownloadSVG } from "App/common/icons-v2/download.svg";
import { CopyDownloadLinkDialog } from "App/common/CopyDownloadLinkDialog";
import { getAppDownloadUrl, isInstagramBrowser } from "App/modules/consts";

/**
 * The shared "Download the App" CTA. Used wherever web sends the user to install
 * the app: the space/track top-right, the countdown panel, and the app-prompt
 * dialog. Hardcodes the theme, download icon, label, and the platform-aware
 * download URL; every other Button prop (size, fullWidth, className, …) passes
 * through, and any of the defaults can be overridden by the caller.
 *
 * The URL is resolved at render and passed as `href`, so this renders a real
 * anchor rather than navigating from onClick: in-app browsers (notably
 * Instagram's) swallow JS-initiated navigation but honor a genuine link tap.
 * In the Instagram browser even the link tap sometimes never redirects, so a
 * tap there also opens a copy-the-link dialog behind the navigation as a
 * fallback; if the redirect lands, the user never sees it.
 */
export const DownloadAppButton = ({
  children,
  ...props
}: Omit<ButtonProps, "ref">) => {
  const [isCopyLinkDialogOpen, setIsCopyLinkDialogOpen] = useState(false);

  return (
    <>
      <Button
        theme={BUTTON_THEME.CTA}
        endIcon={<DownloadSVG />}
        href={getAppDownloadUrl()}
        onClick={() => {
          if (isInstagramBrowser()) {
            setIsCopyLinkDialogOpen(true);
          }
        }}
        {...props}
      >
        {children ?? "Download the App"}
      </Button>
      <CopyDownloadLinkDialog
        open={isCopyLinkDialogOpen}
        onRequestClose={() => setIsCopyLinkDialogOpen(false)}
      />
    </>
  );
};
